import React, { useState } from 'react';
import styled from 'styled-components';
import Panel from './Panel';
import PeriodSelector from './PeriodSelector';
import Pagination from './Pagination';
import useMonitoringData from '../hooks/useMonitoring';
import { fetchSiteHistory } from '../api/monitoringApi';

const Table = styled.table`width: 100%; border-collapse: collapse; font-size: 13px;`;
const Th = styled.th`text-align: left; padding: 8px; background: #f9fafb; border-bottom: 1px solid #e5e7eb; color: #6b7280; font-weight: 600; white-space: nowrap;`;
const Td = styled.td`padding: 8px; border-bottom: 1px solid #f3f4f6; color: #374151;`;

const StatusBadge = styled.span`
  font-size: 11px; padding: 2px 8px; border-radius: 10px; font-weight: 600;
  background: ${p => p.$s === 'approved' ? '#d1fae5' : p.$s === 'deleted' ? '#fee2e2' : '#fef3c7'};
  color: ${p => p.$s === 'approved' ? '#047857' : p.$s === 'deleted' ? '#dc2626' : '#b45309'};
`;

const ExtTypeBadge = styled.span`
  font-size: 10px; padding: 1px 6px; border-radius: 4px; font-weight: 600; margin-right: 4px;
  background: ${p => {
    switch(p.$t) {
      case 'block': return '#dbeafe';
      case 'timer': return '#fef3c7';
      case 'edit': case 'edit_rolling': return '#d1fae5';
      case 'popup': return '#fce7f3';
      case 'floating': return '#e0f2fe';
      case 'rolling_banner': return '#ede9fe';
      default: return '#f3f4f6';
    }
  }};
  color: ${p => {
    switch(p.$t) {
      case 'block': return '#1d4ed8';
      case 'timer': return '#b45309';
      case 'edit': case 'edit_rolling': return '#047857';
      case 'popup': return '#be185d';
      case 'floating': return '#0369a1';
      case 'rolling_banner': return '#6d28d9';
      default: return '#6b7280';
    }
  }};
`;

const ExpandRow = styled.tr`background: #f9fafb;`;
const ExpandTd = styled.td`padding: 8px 8px 8px 32px;`;
const ExtTable = styled.table`width: 100%; border-collapse: collapse; font-size: 12px;`;
const ExtTh = styled.th`text-align: left; padding: 4px 8px; color: #9ca3af; font-weight: 500;`;
const ExtTd = styled.td`padding: 4px 8px; color: #374151;`;

const ToggleBtn = styled.button`
  background: none; border: none; cursor: pointer; font-size: 12px; color: #667eea;
  &:hover { text-decoration: underline; }
`;

const Summary = styled.div`display: flex; gap: 16px; margin-bottom: 12px; flex-wrap: wrap;`;
const SummaryItem = styled.div`font-size: 13px; color: #6b7280; span { font-weight: 700; color: #111827; }`;

function fmtDate(iso) { return iso ? new Date(iso).toLocaleDateString('ko-KR') : '-'; }

export default function SiteHistoryPanel({ refreshKey }) {
  const [period, setPeriod] = useState('30d');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState({});
  const { data, isLoading, error } = useMonitoringData(
    () => fetchSiteHistory(period, page, 20), [period, page, refreshKey]
  );

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  const handlePeriod = (p) => { setPeriod(p); setPage(1); };

  return (
    <Panel title="사이트 등록 이력" isLoading={isLoading} error={error}
      actions={<PeriodSelector value={period} onChange={handlePeriod} options={['7d','30d','90d']} />}>
      {data && <>
        <Summary>
          <SummaryItem>기간 내 등록: <span>{data.total_count}개</span></SummaryItem>
          {Object.entries(data.by_status || {}).map(([k, v]) => (
            <SummaryItem key={k}>{k}: <span>{v}</span></SummaryItem>
          ))}
        </Summary>
        <div style={{ overflowX: 'auto' }}>
          <Table>
            <thead><tr>
              <Th></Th><Th>사이트</Th><Th>도메인</Th><Th>소유자</Th><Th>플랜</Th><Th>익스텐션</Th><Th>상태</Th><Th>마지막 방문</Th><Th>등록일</Th>
            </tr></thead>
            <tbody>
              {data.records?.map((r) => (
                <React.Fragment key={r.site_id}>
                  <tr>
                    <Td>
                      {r.extension_count > 0 && (
                        <ToggleBtn onClick={() => toggle(r.site_id)}>
                          {expanded[r.site_id] ? '▼' : '▶'}
                        </ToggleBtn>
                      )}
                    </Td>
                    <Td>{r.site_name}</Td>
                    <Td>{r.domain}</Td>
                    <Td>{r.client_name || r.client_email}</Td>
                    <Td>{r.active_plan || <span style={{color:'#9ca3af'}}>미구독</span>}</Td>
                    <Td>{r.extension_count > 0 ? `${r.extension_count}개` : <span style={{color:'#9ca3af'}}>없음</span>}</Td>
                    <Td><StatusBadge $s={r.status}>{r.status}</StatusBadge></Td>
                    <Td>{fmtDate(r.visited_at)}</Td>
                    <Td>{fmtDate(r.created_at)}</Td>
                  </tr>
                  {expanded[r.site_id] && r.extensions?.length > 0 && (
                    <ExpandRow>
                      <ExpandTd colSpan={9}>
                        <ExtTable>
                          <thead><tr><ExtTh>익스텐션</ExtTh><ExtTh>타입</ExtTh><ExtTh>설정 제목</ExtTh><ExtTh>상태</ExtTh></tr></thead>
                          <tbody>
                            {r.extensions.map((e, j) => (
                              <tr key={j}>
                                <ExtTd>{e.extension_title}</ExtTd>
                                <ExtTd><ExtTypeBadge $t={e.extension_type}>{e.extension_type}</ExtTypeBadge></ExtTd>
                                <ExtTd>{e.config_title || '-'}</ExtTd>
                                <ExtTd><StatusBadge $s={e.config_status}>{e.config_status}</StatusBadge></ExtTd>
                              </tr>
                            ))}
                          </tbody>
                        </ExtTable>
                      </ExpandTd>
                    </ExpandRow>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </Table>
        </div>
        <Pagination page={data.page} totalPages={data.total_pages} onChange={setPage} />
      </>}
    </Panel>
  );
}
